/* FILE: ./frontend/src/features/auth/AccountSettings.jsx */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function AccountSettings() {
  const { t } = useTranslation();
  const { user, isSupplier, supplierId, signOut, refreshContext } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [simsRemainingServer, setSimsRemainingServer] = useState(null);
  const [simLimitServer, setSimLimitServer] = useState(null);
  const [simsUsed, setSimsUsed] = useState(0);
  const [loading, setLoading] = useState(true);

  // Password Update State
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [msg, setMsg] = useState({ type: '', text: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const subscriptionRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    
    const fetchProfile = async () => {
      if (!user) {
        if (mounted) setLoading(false);
        return;
      }
      
      try {
        // ⚡ Reconcile any Helcim Void/Reverse or Refund before reading profile balances
        const syncRes = await axios.post(
          `${API_BASE}/payments/helcim-sync`,
          {},
          { headers: { 'x-user-id': user.id } }
        ).catch(() => null);

        if (syncRes?.data?.revoked && refreshContext) {
          await refreshContext();
        }

        const currentMonth = new Date().toISOString().slice(0, 7);
        const [{ data, error }, { data: simData }, billingRes] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
          supabase.from('simulator_usage').select('usage_count').eq('user_id', user.id).eq('month', currentMonth).maybeSingle(),
          axios.get(`${API_BASE}/admin/users/${user.id}/billing`).catch(() => null)
        ]);
          
        if (error) console.warn("Profile fetch warning:", error.message);
        
        if (mounted) {
          if (data) setProfile(data);
          if (billingRes?.data) {
            setSimsRemainingServer(billingRes.data.simulations_remaining);
            setSimLimitServer(billingRes.data.sim_limit);
          } else {
            setSimsUsed(simData?.usage_count || 0);
          }
        }
      } catch (err) {
        console.error("Account Settings Error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    
    fetchProfile();
    return () => { mounted = false; };
  }, [user]); // eslint-disable-line

  useEffect(() => {
    return () => {
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    };
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });

    if (password !== confirmPassword) {
      setMsg({ type: 'error', text: t('settings.pwd_match_err', 'Passwords do not match.') });
      return;
    }
    if (password.length < 6) {
      setMsg({ type: 'error', text: t('settings.pwd_len_err', 'Password must be at least 6 characters.') });
      return;
    }

    setIsSaving(true);
    
    try {
      const { error } = await supabase.auth.updateUser({ password: password });

      if (error) {
        if (error.status === 422 || error.status === 406 || error.message?.includes("different")) {
             throw new Error(t('settings.pwd_diff_err', 'Must be a different password.'));
        }
        throw error;
      }

      setMsg({ type: 'success', text: t('settings.pwd_success', 'Password successfully updated.') });
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMsg({ type: 'error', text: err.message || t('settings.pwd_fail', 'Failed to update password.') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenPortal = () => {
    window.open('https://tariffbeater-inc.helcim.app/login', '_blank');
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('settings.del_warn_1'))) return;
    if (!window.confirm(t('settings.del_warn_2'))) return;

    setIsDeleting(true);
    try {
      if (isSupplier && supplierId) {
        await axios.delete(`${API_BASE}/suppliers/${supplierId}`);
      } else if (user?.id) {
        await axios.delete(`${API_BASE}/suppliers/cleanup/${user.id}`);
      }
      await signOut();
      alert(t('settings.del_success'));
      window.location.href = '/';
    } catch (err) {
      if (err.response?.status === 404) {
        await signOut();
        window.location.href = '/';
      } else {
        alert(t('settings.del_fail') + (err.response?.data?.error || err.message));
        setIsDeleting(false);
      }
    }
  };

  if (loading) return <div className="p-12 text-center">{t('settings.loading')}</div>;

  const isAdmin = profile?.role === 'admin' || user?.email?.toLowerCase() === 'admin@shaikh.net.in';
  const isProBuyer = profile?.buyer_tier === 'pro' || profile?.buyer_tier === 'pro_volume';
  const isHighVol = profile?.buyer_volume_tier === 'high_volume' || profile?.buyer_tier === 'pro_volume';
  const simLimit = simLimitServer !== null ? simLimitServer : (isProBuyer ? (isHighVol ? 300 : 100) : 3);
  const simsRemaining = simsRemainingServer !== null ? simsRemainingServer : Math.max(0, simLimit - simsUsed);
  const auditCap = isProBuyer ? (isHighVol ? 300 : 100) : 0;

  const hasActivePaidAccess = Boolean(
    profile?.helcim_sub_id ||
    isProBuyer ||
    profile?.supplier_tier === 'premium' ||
    profile?.has_analytics ||
    (profile?.connection_credits || 0) > 0
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 text-left">
      <div className="max-w-4xl mx-auto space-y-6">
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          {t('settings.title')}
        </h1>

        {/* PROFILE & SUBSCRIPTION CARD */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h2 className="text-xl font-bold text-gray-800">{t('settings.acct_profile')}</h2>
            {isAdmin && (
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold uppercase">
                {t('settings.admin_badge', 'Administrator')}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            
            {/* Left Column: Core Info */}
            <div className="space-y-4 text-left">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">{t('settings.email')}</label>
                <p className="text-gray-900 font-medium">{user.email}</p>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">{t('settings.phone', 'Phone Number')}</label>
                <p className="text-gray-900 font-medium">
                  {profile?.phone || user?.user_metadata?.phone || "Not Provided"}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">{t('settings.role')}</label>
                <div className="mt-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${isSupplier ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                    {isSupplier ? t('settings.role_supplier') : t('settings.role_buyer')}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase">{t('settings.created')}</label>
                <p className="text-gray-900">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            {/* Right Column: Modular Billing State & True Leftover Counters */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 space-y-4 text-left">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider border-b border-gray-200 pb-2">{t('settings.billing')}</h3>
              
              {/* BUYER SECTION */}
              <div className="space-y-2.5">
                <div className="text-[10px] font-black text-emerald-700 uppercase tracking-wider">🛒 Buyer Access & Balances</div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{t('settings.buyer_sim')}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${isProBuyer ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-200 text-gray-700'}`}>
                    {isProBuyer 
                      ? (isHighVol ? t('settings.pro_300') : t('settings.pro_100')) 
                      : t('settings.free_3')}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{t('settings.sims_remaining', 'Simulations Remaining (This Month)')}</span>
                  <span className="text-sm font-black text-indigo-700 font-mono">{simsRemaining} / {simLimit}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{t('settings.audits_remaining', 'Deep AI Audits Remaining')}</span>
                  <span className="text-sm font-black text-indigo-700 font-mono">
                    {profile?.ai_audit_credits || 0}{auditCap > 0 ? ` / ${auditCap}` : ''}
                  </span>
                </div>
              </div>

              {/* SUPPLIER SECTION */}
              <div className="space-y-2.5 pt-3 border-t border-gray-200">
                <div className="text-[10px] font-black text-blue-700 uppercase tracking-wider">🏭 Supplier Access & Balances</div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{t('settings.sup_analytics')}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${profile?.has_analytics ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                    {profile?.has_analytics ? t('settings.unlocked') : t('settings.locked')}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">{t('settings.prem_sup')}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${profile?.supplier_tier === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-700'}`}>
                    {profile?.supplier_tier === 'premium' ? t('settings.prem_1000') : t('settings.std_50')}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-1">
                  <span className="text-sm text-gray-600 font-bold">RFQ Reply Credits</span>
                  <span className="text-lg font-black text-blue-600 font-mono">{profile?.connection_credits || 0}</span>
                </div>
              </div>

              {(profile?.supplier_expires_at || profile?.buyer_expires_at) && (
                <div className="text-[11px] text-gray-500 bg-white p-2.5 rounded border border-gray-200 space-y-1">
                  {profile?.supplier_expires_at && (
                    <div>Supplier Cycle Valid Until: <strong>{new Date(profile.supplier_expires_at).toLocaleDateString()}</strong></div>
                  )}
                  {profile?.buyer_expires_at && (
                    <div>Buyer Cycle Valid Until: <strong>{new Date(profile.buyer_expires_at).toLocaleDateString()}</strong></div>
                  )}
                  <div className="text-[10px] text-amber-700 font-medium">Unused monthly credits do not roll over.</div>
                </div>
              )}

              <div className="text-center pt-2">
                <button 
                  onClick={() => navigate('/billing')} 
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {t('settings.manage_subs')}
                </button>
              </div>

              <div className="text-center pt-2 mt-2 border-t border-gray-200">
                <button 
                  onClick={handleOpenPortal}
                  disabled={loading || !hasActivePaidAccess}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors block w-full ${
                    !hasActivePaidAccess 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-gray-900 text-white hover:bg-black shadow-sm'
                  }`}
                >
                  {t('settings.manage_billing_invoices', 'Manage Billing & Invoices')}
                </button>
                {!hasActivePaidAccess && (
                  <p className="text-[10px] text-gray-400 mt-1 italic text-center">Available after your first upgrade</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECURITY CARD */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-left">
          <h2 className="text-xl font-bold text-gray-800 mb-4">{t('settings.security', 'Security & Authentication')}</h2>
          <p className="text-sm text-gray-600 mb-6">
            Log in instantly using secure email codes, or create an optional password for this account.
          </p>
          <form onSubmit={handleUpdatePassword} className="space-y-4 max-w-md">
            <input 
              type="text" 
              name="username" 
              value={user.email} 
              autoComplete="username" 
              readOnly
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, padding: 0, border: 0 }}
            />

            {msg.text && (
              <div className={`p-4 rounded-lg text-sm font-bold flex items-center ${msg.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                {msg.type === 'success' ? '✅ ' : '⚠️ '} {msg.text}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{t('settings.new_pwd', 'Set or Update Password')}</label>
              <input 
                type="password" 
                required
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{t('settings.confirm_pwd', 'Confirm Password')}</label>
              <input 
                type="password" 
                required
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••"
              />
            </div>

            <button 
              type="submit" 
              disabled={isSaving || !password}
              className={`w-full md:w-auto font-bold py-2 px-6 rounded-lg shadow-sm transition-all ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
              {isSaving ? t('settings.updating', 'Saving...') : t('settings.update_btn', 'Save Password')}
            </button>
          </form>
        </div>

        {/* DANGER ZONE */}
        <div className="border border-red-200 rounded-lg overflow-hidden mt-8 text-left">
          <div className="bg-red-50 p-4 border-b border-red-100">
            <h3 className="text-red-800 font-bold flex items-center gap-2">
              <span>⚠️</span> {t('settings.danger')}
            </h3>
          </div>
          <div className="p-6 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h4 className="text-gray-900 font-bold">{t('settings.del_acct')}</h4>
              <p className="text-gray-600 text-sm mt-1">{t('settings.del_desc')}</p>
            </div>
            <button 
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="whitespace-nowrap bg-white border-2 border-red-100 text-red-600 hover:bg-red-600 hover:border-red-600 hover:text-white px-5 py-2.5 rounded-lg font-bold transition-all shadow-sm"
            >
              {isDeleting ? t('settings.deleting') : t('settings.del_acct')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}